import downloadBlob from "./downloadBlob";
import getMIMETypeByFileName from "./getMIMETypeByFileName";

/**
 * download file from different origin including credentials + cookie
 *
 * the object url will be revoked after download
 *
 * this method is used to download file from other domain since <a> attr: download only works for the same-origin url
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-download
 * @param url download file link
 * @param [fileName=""] optional. download file name
 */
export default async function downloadFromDifferentOrigin(
  url: string,
  fileName = "",
  fetchOptions?: Omit<RequestInit, "credentials" | "headers">,
  callback?: (arg: { receivedLength: number; contentLength: number }) => void,
) {
  let nextFileName = fileName;
  // determine a file name by download url
  if (!fileName) {
    nextFileName = url.split("/").pop() || "download";
  }
  // Step 1: start the fetch and obtain a reader

  const response = await fetch(url, {
    credentials: "include",
    headers: {
      cookie: document.cookie,
    },
    ...fetchOptions,
  });

  const reader = (response.body as any).getReader();

  // Step 2: get total length
  const contentLength = +(response.headers.get("Content-Length") || 0);
  if (callback) {
    callback({
      receivedLength: 0,
      contentLength,
    });
  }
  const dataType =
    response.headers.get("content-type") || getMIMETypeByFileName(url);
  // Step 3: read the data
  let receivedLength = 0; // received that many bytes at the moment
  let chunks: any[] = []; // array of received binary chunks (comprises the body)

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      if (callback) {
        callback({
          receivedLength: contentLength,
          contentLength,
        });
      }
      break;
    }

    chunks.push(value);
    receivedLength += value.length;
    if (callback) {
      callback({
        receivedLength,
        contentLength,
      });
    }
  }

  // Step 4: concatenate chunks into single Uint8Array
  let chunksAll = new Uint8Array(receivedLength); // (4.1)
  let position = 0;
  for (let chunk of chunks) {
    chunksAll.set(chunk, position); // (4.2)
    position += chunk.length;
  }

  // It is necessary to create a new blob object with mime-type explicitly set for all browsers except Chrome, but it works for Chrome too.
  const newBlob = new Blob([chunksAll], {
    type: dataType,
  });

  return downloadBlob(newBlob, nextFileName);
}
